import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  searchSupportBusiness,
  getSupportBusinessDetails,
} from "../../../api/customerSupport";

const BusinessAutocomplete = ({ onBusinessSelected, error }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const [placeId, setPlaceId] = useState(null);
  const [businessName, setBusinessName] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);

  useEffect(() => {
    if (!isTyping) return;

    const timeout = setTimeout(() => {
      if (searchQuery.length >= 4) {
        handleSearch(searchQuery);
      } else {
        setSuggestions([]);
      }
    }, 400);

    return () => clearTimeout(timeout);
  }, [searchQuery, isTyping]);

  const handleSearch = async (value) => {
    if (value.length < 4) {
      setSuggestions([]);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    try {
      const results = await searchSupportBusiness(value);
      setSuggestions(results?.length ? results : []);
    } catch {
      setSuggestions([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const resetSelection = () => {
    setPlaceId(null);
    setBusinessName("");
    setSearchQuery("");
    setSuggestions([]);
    onBusinessSelected(null);
  };

  const handleSelect = async (place) => {
    setIsTyping(false);
    setSuggestions([]);
    setIsFocused(false);
    setDetailsLoading(true);

    try {
      const data = await getSupportBusinessDetails(place.place_id);
      setPlaceId(place.place_id);
      setBusinessName(data.name || place.structured_formatting?.main_text || "");

      let countryCode = "";
      let phoneNumber = "";
      if (data.international_phone_number) {
        const intlPhone = data.international_phone_number;
        countryCode = intlPhone.match(/^\+?\d+/)?.[0] || "";
        phoneNumber = intlPhone.replace(countryCode, "").replace(/[^\d]/g, "");
        if (countryCode && !countryCode.startsWith("+")) {
          countryCode = `+${countryCode}`;
        }
      }

      onBusinessSelected({
        placeId: place.place_id,
        businessName: data.name || "",
        email: data.email || "",
        countryCode,
        phoneNumber,
        website: data.website || "",
        address: data.formatted_address || "",
        businessType: data.businessType || "hotel",
      });
    } catch {
      toast.error("Could not load business details from Google.");
      resetSelection();
    } finally {
      setDetailsLoading(false);
    }
  };

  return (
    <div className="relative">
      <label
        htmlFor="business-search"
        className="block text-sm font-medium mb-2 text-gray-700"
      >
        Your business
      </label>
      <div className="relative">
        <input
          id="business-search"
          name="business-search"
          type="text"
          autoComplete="off"
          value={placeId ? businessName : searchQuery}
          onChange={(e) => {
            const value = e.target.value;
            if (placeId) {
              resetSelection();
              setSearchQuery(value);
              setIsTyping(true);
              return;
            }
            setIsTyping(true);
            setSearchQuery(value);
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() =>
            setTimeout(() => {
              setIsFocused(false);
              setIsTyping(false);
              if (!placeId) setSearchQuery("");
            }, 150)
          }
          placeholder="Search your business on Google"
          className="block w-full rounded-md border border-gray-300 px-4 py-3 sm:py-2.5 text-base sm:text-sm pr-10 focus:border-indigo-500 focus:ring-indigo-500 placeholder:text-gray-400"
        />
        {(searchLoading || detailsLoading) && (
          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
            <svg
              className="h-5 w-5 animate-spin text-indigo-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        )}
      </div>

      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}

      {isFocused && !placeId && suggestions.length > 0 && (
        <ul className="absolute z-20 mt-1 w-full bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
          {suggestions.map((s, i) => (
            <li key={s.place_id || i}>
              <button
                type="button"
                onMouseDown={() => handleSelect(s)}
                className="w-full text-left px-4 py-3 hover:bg-gray-100 active:bg-gray-200"
              >
                <div className="font-medium text-sm">
                  {s.structured_formatting?.main_text}
                </div>
                <div className="text-xs text-gray-500">
                  {s.structured_formatting?.secondary_text}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {!placeId && searchQuery.length > 0 && searchQuery.length < 4 && !searchLoading && (
        <p className="mt-2 text-sm text-amber-600">
          Type at least 4 characters to search.
        </p>
      )}

      {placeId && (
        <p className="mt-2 text-sm text-green-600">
          Business selected. Type again to search for a different business.
        </p>
      )}
    </div>
  );
};

export default BusinessAutocomplete;
